import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MenuRegenerationService } from 'src/app/services/menu-regeneration.service';
import { ReservationsStackFeatureService } from 'src/app/services/reservations-stack-feature.service';
import { WhiteLabelService } from 'src/app/services/white-label.service';
import { DataService } from 'src/app/services/data.service';
import { MENU_ITEM_TITLE_KEYS } from '../constants/menu-item-title-keys.constants';
import {
  SIDEBAR_DEFAULT_LOGO_FULL_URL,
  SIDEBAR_DEFAULT_LOGO_RAIL_URL,
} from '../constants/sidebar-logo.constants';
import {
  isSidebarManuallyCollapsed,
  SidebarManualExpandKey,
  SidebarManualExpandState,
} from '../constants/sidebar-manual-expand-state.enum';
import { MenuSectionFeature, ROUTES_V2 } from '../menu-items-v2';
import {
  EMPTY_SIDEBAR_MENU_BADGE_COUNTS,
  SidebarMenuBadgeCounts,
} from '../models/sidebar-menu-badge-counts.model';
import { SidebarMenuBadgesService } from '../services/sidebar-menu-badges.service';
import { SidebarMenuBuilderV2Service } from '../services/sidebar-menu-builder-v2.service';
import { RouteInfo } from '../sidebar.metadata';
import { WhiteLabelTypes } from '../sidebar.component';

@Component({
  selector: 'app-sidebar-v2',
  templateUrl: './sidebar-v2.component.html',
  styleUrls: ['./sidebar-v2.component.scss'],
})
export class SidebarV2Component implements OnInit, OnDestroy {
  public showMenu: SidebarManualExpandKey = SidebarManualExpandState.Unset;
  public activeMenu = '';
  public showSubMenu: SidebarManualExpandKey = SidebarManualExpandState.Unset;
  public currentUrl = '';
  public isRailMode = false;
  public sidebarnavItems: RouteInfo[] = [];
  public bottomNavItems: RouteInfo[] = [];
  public logoRailMode = SIDEBAR_DEFAULT_LOGO_RAIL_URL;
  public logoFullMode = SIDEBAR_DEFAULT_LOGO_FULL_URL;
  public badgeCounts: SidebarMenuBadgeCounts = { ...EMPTY_SIDEBAR_MENU_BADGE_COUNTS };
  public readonly menuItemTitleKeys = MENU_ITEM_TITLE_KEYS;

  private readonly defaultExpandedSectionTitle = ROUTES_V2[MenuSectionFeature.operationalDropdown].title;
  private menuRegenerationSubscription?: Subscription;
  private stackReadySubscription?: Subscription;
  private badgesSubscription?: Subscription;
  private routerSubscription?: Subscription;
  private buildSubscription?: Subscription;

  constructor(
    public dataService: DataService,
    private router: Router,
    private menuRegenerationService: MenuRegenerationService,
    private reservationsStackFeature: ReservationsStackFeatureService,
    private whiteLabelService: WhiteLabelService,
    private menuBuilder: SidebarMenuBuilderV2Service,
    private menuBadges: SidebarMenuBadgesService,
  ) {}

  ngOnInit(): void {
    this.isRailMode = window.innerWidth < 768;
    this.setupMenuRegeneration();
    this.setupRouterEvents();
    this.setLogoUrl();
    this.reservationsStackFeature.whenReady?.().subscribe();
    // One probe → stamp áreas/reservas paths → build menu once (ready$ is distinctUntilChanged).
    this.stackReadySubscription = this.reservationsStackFeature.ready$
      .pipe(filter((ready) => ready === true))
      .subscribe(() => {
        this.rebuildMenu();
      });
  }

  ngOnDestroy(): void {
    this.menuRegenerationSubscription?.unsubscribe();
    this.stackReadySubscription?.unsubscribe();
    this.badgesSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
    this.buildSubscription?.unsubscribe();
  }

  public regenerateMenu(): void {
    this.setLogoUrl();
    if (this.reservationsStackFeature.isReady === false) {
      return;
    }
    this.rebuildMenu();
  }

  private rebuildMenu(): void {
    this.buildSubscription?.unsubscribe();
    this.buildSubscription = this.menuBuilder.buildMenu().subscribe((result) => {
      this.sidebarnavItems = result.sidebarnavItems;
      this.bottomNavItems = result.bottomNavItems;
      this.currentUrl = this.router.url;
      this.expandMenuIfRouteMatches();
    });
    this.menuBadges.refresh();
    this.badgesSubscription?.unsubscribe();
    this.badgesSubscription = this.menuBadges.counts$.subscribe((counts) => {
      this.badgeCounts = counts;
    });
  }

  private setupMenuRegeneration(): void {
    this.menuRegenerationSubscription = this.menuRegenerationService.menuRegeneration$.subscribe(() => {
      this.regenerateMenu();
    });
  }

  private setupRouterEvents(): void {
    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentUrl = event.url;
        this.showMenu = SidebarManualExpandState.Collapsed;
        this.expandMenuIfRouteMatches();
      }
    });
  }

  public toggleRailMode(): void {
    this.isRailMode = !this.isRailMode;
    this.showMenu = SidebarManualExpandState.Collapsed;
  }

  public isActive(itemPath: string): boolean {
    if (!itemPath) {
      return false;
    }
    return this.currentUrl === itemPath || this.currentUrl.startsWith(`${itemPath}/`);
  }

  public isExpanded(title: string): boolean {
    return this.activeMenu === title || this.showMenu === title;
  }

  public addExpandClass(element: string): void {
    if (element === this.showMenu) {
      this.showMenu = SidebarManualExpandState.Collapsed;
    } else {
      this.showMenu = element;
      this.isRailMode = false;
    }
  }

  public addActiveClass(element: string): void {
    this.showSubMenu =
      element === this.showSubMenu ? SidebarManualExpandState.Collapsed : element;
  }

  public handleMenuItemClick(item: RouteInfo): void {
    if (item.externalLink || /^https?:\/\//i.test(item.path)) {
      window.open(item.path, '_blank');
      (document.activeElement as HTMLElement)?.blur?.();
      return;
    }
    if (item.submenu?.length > 0) {
      this.addExpandClass(item.title);
      return;
    }
    this.addActiveClass(item.title);
    // Canonical stack URL from probe (avoids stale legacy path on the RouteInfo object).
    const target = this.reservationsStackFeature.resolveMenuPath(item.path);
    void this.router.navigateByUrl(target);
  }

  public setLogoUrl(): void {
    const building = this.dataService.getBuilding();
    const logoRailUrl = this.whiteLabelService.getMenuLogoRailUrl(building);
    const logoFullUrl = this.whiteLabelService.getMenuLogoFullUrl(building);

    if (logoRailUrl) {
      this.logoRailMode = logoRailUrl;
    }
    if (logoFullUrl) {
      this.logoFullMode = logoFullUrl;
    }

    if (!logoRailUrl && !logoFullUrl) {
      const whiteLabelConfig = building?.config?.whiteLabel;
      if (whiteLabelConfig?.type === WhiteLabelTypes.MTS) {
        const companyConfig = whiteLabelConfig.params?.dashboard?.company;
        if (companyConfig?.logoRail) {
          this.logoRailMode = companyConfig.logoRail;
        }
        if (companyConfig?.logoFull) {
          this.logoFullMode = companyConfig.logoFull;
        }
      }
    }
  }

  private expandMenuIfRouteMatches(): void {
    if (!this.currentUrl) {
      return;
    }

    let menuToExpand = '';
    for (const menuItem of this.sidebarnavItems) {
      if (menuItem.submenu?.length > 0) {
        const matchingSubmenu = menuItem.submenu.find(
          (subItem) =>
            subItem.path &&
            (this.currentUrl === subItem.path || this.currentUrl.startsWith(`${subItem.path}/`)),
        );
        if (matchingSubmenu) {
          menuToExpand = menuItem.title;
          break;
        }
      }
    }

    if (menuToExpand) {
      this.activeMenu = menuToExpand;
      return;
    }

    this.activeMenu = '';
    if (isSidebarManuallyCollapsed(this.showMenu)) {
      this.showMenu = this.getDefaultExpandedSectionTitle();
    }
  }

  private getDefaultExpandedSectionTitle(): string {
    const hasDefaultSection = this.sidebarnavItems.some((item) => item.title === this.defaultExpandedSectionTitle);
    return hasDefaultSection ? this.defaultExpandedSectionTitle : SidebarManualExpandState.Unset;
  }
}
